import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { WatchedSignal } from '@/components/blocks/WatchedSignal';
import { Button, Text } from '@/components/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { userStore } from '@/stores/user.store';

import { useCardWatcherRoutinesQuery } from '../card-watchers.query';

export const InternalRoutinesSection = observer(
  function InternalRoutinesSection({ children }: { children: ReactNode }) {
    const { data: watchers = [], isLoading } = useCardWatcherRoutinesQuery();
    const navigate = useNavigate();
    if (!userStore.isInternalUser) return null;

    return (
      <Collapsible className="group" defaultOpen={false}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-foreground-muted hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
          <Icons.chevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
          Internal routines
        </CollapsibleTrigger>
        <CollapsibleContent>
          <YStack className="gap-2 pt-2">
            {children}
            {isLoading ? null : watchers.length > 0 ? (
              watchers.map(({ routineId, agent }) => (
                <button
                  key={routineId}
                  type="button"
                  data-testid="card-watcher-row"
                  className="flex min-w-0 flex-col gap-0.5 rounded-2xl px-3 py-2 text-left transition-colors hover:bg-surface-hover"
                  onClick={() =>
                    void navigate({
                      to: '/routines/$routineId',
                      params: { routineId: String(routineId) },
                    })
                  }
                >
                  <XStack className="min-w-0 items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-foreground">
                      Card watcher
                    </span>
                    <WatchedSignal
                      watched
                      label="The watcher is checking this agent's active cards."
                    />
                  </XStack>
                  <span className="truncate text-xs text-foreground-muted">
                    {agent
                      ? `Checks ${agent}'s active cards every 30 minutes`
                      : 'Checks your active cards every 30 minutes'}
                  </span>
                </button>
              ))
            ) : (
              <Text className="px-2 text-xs text-foreground-muted">
                No watcher yet. Each proactive agent's watcher starts with its
                next card.
              </Text>
            )}
          </YStack>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

/** Opens the watcher routine that checked a card, where its prompt, schedule and every run are */
export const OpenWatcherButton = ({
  routineId,
}: {
  routineId: number | null | undefined;
}) => {
  const navigate = useNavigate();
  if (routineId == null) return null;

  return (
    <Button
      variant="secondary"
      size="xs"
      data-testid="open-card-watcher"
      className="mt-2 self-start"
      onClick={() =>
        void navigate({
          to: '/routines/$routineId',
          params: { routineId: String(routineId) },
        })
      }
    >
      Open watcher
    </Button>
  );
};
